<script setup lang="ts">
/**
 * Check · what the rebuild changed (shelf-board-assembly plan D5): when a family had to change the
 * layout read from the photo to meet its construction rules, this card says what changed and why.
 * A repair that changed the photographed design a lot is held for review: export waits until the
 * user accepts the changes here, or changes the layout in Shape (which marks the hold edited).
 */
import { computed } from 'vue';
import Icon from './Icon.vue';
import ProvChip from './ProvChip.vue';
import { snap } from '@/app/state';
import { announce } from '@/app/announce';
import { confirmKey } from '@/app/edits';
import { go, select } from '@/app/navigation';
import { fitHistoryOf } from '@/families/repair-history';
import { plural } from '@/app/format';

const params = computed(() => (snap.value.design.params ?? {}) as Record<string, unknown>);
/**
 * The family's own sentences for each change ("Added a left post in tier 2 …"). Repeated post moves
 * (the repair moves posts in steps) collapse to the last, which is where they ended up.
 */
const notes = computed(() => {
  const out: string[] = [];
  for (const c of (params.value.buildableChanges as string[] | undefined) ?? []) {
    const k = /^Moved the posts /.test(c) ? out.findIndex((x) => /^Moved the posts /.test(x)) : out.indexOf(c);
    if (k >= 0) out.splice(k, 1);
    out.push(c);
  }
  return out;
});
const history = computed(() => fitHistoryOf(params.value));
const hold = computed(() => history.value?.hold ?? null);
const field = computed(() => snap.value.fields.repairs ?? null);
const pending = computed(() => !!field.value && field.value.status === 'unresolved');
const added = computed(() => (hold.value?.addedParts ?? []).filter((id) => snap.value.build.parts.some((p) => p.id === id)));
const removed = computed(() => hold.value?.removedParts ?? []);
const pct = (v: unknown) => (typeof v === 'number' ? `${Math.round(v * 100)} %` : '');
/** What normalisation dropped, plus the normalisation moves worth seeing (post position, tier heights, shelf ends moved more than 2 %). */
const dropped = computed(() => {
  const out = [...(hold.value?.normalised ?? [])];
  const seen = new Set<string>();
  for (const c of history.value?.changes ?? []) {
    if (c.by !== 'normalise' || c.op === 'remove') continue;
    const big = c.target === 'postInset' || c.target === 'tierWeights' || (/^horizontals\[\d+\]\.x/.test(c.target) && typeof c.from === 'number' && typeof c.to === 'number' && Math.abs(c.from - c.to) > 0.02);
    if (!big || seen.has(c.note)) continue;
    seen.add(c.note);
    out.push(c.target === 'postInset' ? `${c.note}: ${pct(c.from)} → ${pct(c.to)} of the width.` : `${c.note}.`);
  }
  return out;
});
const show = computed(() => notes.value.length > 0 || dropped.value.length > 0 || !!hold.value?.held);
const labelOf = (id: string) => snap.value.build.parts.find((p) => p.id === id)?.label ?? hold.value?.labels?.[id] ?? id;

function accept() {
  confirmKey('repairs', 'Changes to the layout');
  announce('Changes to the layout accepted.');
}
</script>

<template>
  <section v-if="show" class="vcard repairs-card" :class="{ needs: pending }" data-field="repairs" aria-labelledby="repairsTitle">
    <div class="vc-top">
      <h2 id="repairsTitle" class="vc-label">What the rebuild changed</h2>
      <ProvChip v-if="field" :status="field.status" />
    </div>
    <p v-if="hold?.held && pending" class="vc-note" role="note" data-hold><Icon name="lock" /><span><strong>Held for your review.</strong> {{ hold.reasons.join('; ') }}. Nothing is exported until you accept these changes or adjust the layout in Shape.</span></p>
    <p v-else-if="hold?.held && field?.status === 'confirmed'" class="vc-note" role="note"><Icon name="check" /><span>You accepted these changes.</span></p>
    <p v-else-if="hold?.held && field?.status === 'edited'" class="vc-note" role="note"><Icon name="pencil" /><span>You changed the layout yourself after the rebuild.</span></p>
    <p v-else class="muted small">To meet its construction rules the rebuild made {{ plural(notes.length, 'small change') }} to the layout read from the photo.</p>
    <ul v-if="notes.length || dropped.length" class="fam-buildable" data-buildable>
      <li v-for="c in dropped" :key="c">{{ c }}</li>
      <li v-for="c in notes" :key="c">{{ c }}</li>
    </ul>
    <div v-if="added.length || removed.length" class="repairs-parts">
      <p v-if="added.length" class="small"><strong>Added, not in the photo:</strong>
        <button v-for="id in added" :key="id" type="button" class="btn btn--sm btn--quiet" :data-show-part="id" @click="select(id)"><Icon name="eye" /> {{ labelOf(id) }}</button>
      </p>
      <p v-if="removed.length" class="small"><strong>Left out, though the photo shows {{ removed.length === 1 ? 'it' : 'them' }}:</strong> {{ removed.map(labelOf).join(', ') }}</p>
    </div>
    <div v-if="pending" class="vc-acts">
      <button id="confirm-repairs" type="button" class="btn btn--sm btn--soft" data-confirm="repairs" @click="accept"><Icon name="check" /> Accept these changes</button>
      <button type="button" class="btn btn--sm btn--quiet" data-act="adjust-in-shape" @click="go('shape')"><Icon name="pencil" /> Adjust in Shape</button>
    </div>
  </section>
</template>

<style scoped>
.fam-buildable { margin: 10px 0 0; padding-left: 18px; display: grid; gap: 4px; font-size: 13px; color: var(--ink-2); }
.repairs-parts { display: grid; gap: 6px; margin-top: 8px; }
.repairs-parts p { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin: 0; }
</style>
